import type { GhostRecording } from "../lib/ghostReplay";

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDelta(delta: number): string {
  const sign = delta > 0 ? "+" : delta < 0 ? "-" : "";
  return `${sign}${Math.abs(delta).toFixed(1)}s`;
}

/**
 * Compact overlay that shows whether a ghost run is loaded, lets the pilot
 * toggle it, and displays the live ahead/behind delta during a run.
 */
export function GhostReplayHUD({
  recording,
  enabled,
  visible,
  racing,
  delta,
  onToggle,
  onClear,
}: {
  recording?: GhostRecording;
  enabled: boolean;
  visible: boolean;
  racing: boolean;
  delta: number | null;
  onToggle: () => void;
  onClear: () => void;
}) {
  if (!visible) return null;

  const hasGhost = Boolean(recording);
  const ahead = delta !== null && delta < -0.05;
  const behind = delta !== null && delta > 0.05;
  const state = !hasGhost ? "empty" : ahead ? "ahead" : behind ? "behind" : "even";

  return (
    <div className={`ghost-hud ghost-hud--${state}`} data-active={enabled && hasGhost}>
      <div className="ghost-hud__row">
        <button
          type="button"
          className="ghost-hud__toggle"
          onClick={onToggle}
          disabled={!hasGhost}
          aria-pressed={enabled && hasGhost}
          title={hasGhost ? "Race your best-run ghost (G)" : "Finish a run to record a ghost"}
        >
          <span className="ghost-hud__dot" aria-hidden="true" />
          {hasGhost ? (enabled ? "Ghost on" : "Ghost off") : "No ghost yet"}
        </button>
        {hasGhost && (
          <button type="button" className="ghost-hud__clear" onClick={onClear} title="Delete recorded ghost">
            Clear
          </button>
        )}
      </div>
      {hasGhost && (
        <div className="ghost-hud__meta">
          <span>Best {formatTime(recording!.seconds)}</span>
          {enabled && racing && delta !== null ? (
            <strong className="ghost-hud__delta">
              {ahead ? "Ahead " : behind ? "Behind " : "Even "}
              {formatDelta(delta)}
            </strong>
          ) : (
            <span className="ghost-hud__hint">{enabled ? "Racing your best" : "Enable to race"}</span>
          )}
        </div>
      )}
    </div>
  );
}
